<template>
    <div class="px-3">
        <!-- Title -->
        <div class="row">
            <div class="col-12">
                <div class="d-flex mb-3">
                    <div class="mr-auto d-flex align-items-center">
                        <h5 class="text-dark mb-0">Branded Caller ID</h5>
                        <i v-if="hasOrder" class="ml-2 material-icons text-green">check_circle_outline</i>
                        <p v-if="hasOrder" class="ml-2 mb-0 font-120 d-flex align-items-center font-weight-bold">
                            <span class="text-green">[Order Submitted]</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <el-steps
            class="mb-3"
            :active="currentStep - 1"
            align-center
            finish-status="success">
            <el-step title="Choose type" />
            <el-step title="Details" />
            <el-step title="Use Case & LOA" />
            <el-step title="Confirm" />
        </el-steps>

        <!-- Step 1: Choose type (hero + cards combined) -->
        <div
            v-if="currentStep === 1"
            class="row mt-2 justify-content-center">
            <div class="col-md-5">
                <!-- Hero banner -->
                <div class="bcid-hero-card p-4 mb-4">
                    <div class="d-flex">
                        <div class="flex-1">
                            <h3 class="text-white font-weight-bold mb-2">Stop losing deals to "Unknown Caller."</h3>
                            <p class="text-white-muted mb-3" style="font-size: 14px;">
                                80% of people assume unidentified calls are spam. Branded Calling puts your verified name and logo on their screen — so they recognize you, trust you, and answer.
                            </p>
                        </div>
                    </div>
                    <div class="d-flex mt-3">
                        <div class="bcid-stat-card mr-2 flex-1">
                            <div class="bcid-stat-number">4x</div>
                            <div class="bcid-stat-label">more conversations</div>
                        </div>
                        <div class="bcid-stat-card mr-2 flex-1">
                            <div class="bcid-stat-number">15%</div>
                            <div class="bcid-stat-label">callback rate on missed calls</div>
                        </div>
                        <div class="bcid-stat-card flex-1">
                            <div class="bcid-stat-number">78%</div>
                            <div class="bcid-stat-label">answer a known brand</div>
                        </div>
                    </div>
                    <div class="mt-3">
                        <a href="https://aloware.com/products/branded-calling"
                           target="_blank"
                           class="bcid-hero-link">
                            Learn more about Branded Calling <i class="fa fa-arrow-right ml-1"></i>
                        </a>
                    </div>
                </div>

                <!-- Type cards -->
                <div class="d-flex mb-4">
                    <!-- Standard Card -->
                    <div
                        class="bcid-type-card p-3 flex-1 mr-2"
                        :class="{ 'bcid-type-card--selected': form.type === TYPE_STANDARD }"
                        @click="selectType(TYPE_STANDARD)"
                    >
                        <h6 class="font-weight-bold mb-2">Standard</h6>
                        <p class="text-muted mb-2" style="font-size: 12px;">
                            Your verified business name on every call. Fast setup, instant spam protection.
                        </p>
                        <ul class="bcid-feature-list mb-0">
                            <li><i class="fa fa-check text-success mr-2"></i>Verified display name</li>
                            <li><i class="fa fa-check text-success mr-2"></i>Long display name</li>
                        </ul>
                    </div>

                    <!-- Enhanced Card -->
                    <div
                        class="bcid-type-card bcid-type-card--enhanced p-3 flex-1 ml-2"
                        :class="{ 'bcid-type-card--selected': form.type === TYPE_ENHANCED }"
                        @click="selectType(TYPE_ENHANCED)"
                    >
                        <h6 class="font-weight-bold mb-2">
                            Enhanced
                            <el-tag size="mini" type="success" effect="dark" class="ml-1" style="vertical-align: middle;">Recommended</el-tag>
                        </h6>
                        <p class="text-muted mb-2" style="font-size: 12px;">
                            Name, logo, and call reason on screen. The conversation starts with trust — not "Who is this?"
                        </p>
                        <ul class="bcid-feature-list mb-0">
                            <li><i class="fa fa-check text-primary mr-2"></i><strong>Verified display name</strong></li>
                            <li><i class="fa fa-check text-primary mr-2"></i><strong>Brand logo on screen</strong></li>
                            <li><i class="fa fa-check text-primary mr-2"></i>Custom call reason</li>
                        </ul>
                    </div>
                </div>

                <!-- You'll need -->
                <div class="border rounded p-3 mb-4" v-if="form.type">
                    <h6 class="text-uppercase text-muted font-weight-bold mb-2" style="font-size: 12px;">You'll need</h6>
                    <div class="d-flex flex-wrap" style="gap: 8px;">
                        <template v-if="form.type === TYPE_ENHANCED">
                            <span class="bcid-need-tag"><span class="bcid-dot bg-primary"></span>Display name</span>
                            <span class="bcid-need-tag"><span class="bcid-dot bg-primary"></span>Brand logo</span>
                            <span class="bcid-need-tag"><span class="bcid-dot bg-primary"></span>Call reason</span>
                            <span class="bcid-need-tag"><span class="bcid-dot bg-primary"></span>Signed LOA</span>
                        </template>
                        <template v-else>
                            <span class="bcid-need-tag"><span class="bcid-dot bg-success"></span>Display name</span>
                            <span class="bcid-need-tag"><span class="bcid-dot bg-success"></span>Long display name</span>
                            <span class="bcid-need-tag"><span class="bcid-dot bg-success"></span>Signed LOA</span>
                        </template>
                    </div>
                </div>

                <div class="d-flex justify-content-end">
                    <el-button
                        v-if="!hasOrder"
                        type="primary"
                        size="medium"
                        @click="goToStep(2)">
                        Continue
                    </el-button>
                </div>
            </div>
            <div class="col-md-5">
                <div class="bcid-preview-sticky">
                    <branded-caller-id-preview
                        :type="form.type"
                        :company-name="form.display_name"
                        :display-long-name="form.display_long_name"
                        :call-reason="form.display_call_reason"
                        :logo-url="form.display_logo_url"
                    />
                </div>
            </div>
        </div>

        <!-- Step 2: Details Form -->
        <div
            v-if="currentStep === 2"
            class="row mt-2 border p-3 justify-content-center">
            <div class="col-md-6 mb-4 mb-md-0">
                <el-card class="border-0" shadow="never">
                    <div slot="header">
                        <strong>Branded Caller ID Details</strong>
                    </div>
                    <el-form
                        ref="bcidForm"
                        :model="form"
                        :rules="formRules"
                        label-position="top"
                        class="form-aloware">

                        <!-- Display Name -->
                        <el-form-item prop="display_name">
                            <template slot="label">
                                Display Name
                                <el-tooltip
                                    class="item"
                                    effect="dark"
                                    :content="form.type === TYPE_ENHANCED
                                        ? 'Max 35 characters. Use your official legal name, brand name, or DBA. Must start with a letter.'
                                        : 'Max 15 characters. Use your official legal name, brand name, or DBA. Must start with a letter.'"
                                    placement="top">
                                    <i class="fa fa-info-circle text-blue"/>
                                </el-tooltip>
                            </template>
                            <el-input
                                v-model="form.display_name"
                                :maxlength="form.type === TYPE_ENHANCED ? 35 : 15"
                                show-word-limit
                                placeholder="Your Company"
                                @input="preValidateForm('bcidForm')">
                            </el-input>
                        </el-form-item>

                        <!-- Display Long Name (Standard only) -->
                        <el-form-item v-if="form.type === TYPE_STANDARD" prop="display_long_name">
                            <template slot="label">
                                Display Long Name
                                <el-tooltip
                                    class="item"
                                    effect="dark"
                                    content="Maximum 32 characters. Must start with a letter."
                                    placement="top">
                                    <i class="fa fa-info-circle text-blue"/>
                                </el-tooltip>
                            </template>
                            <el-input
                                v-model="form.display_long_name"
                                maxlength="32"
                                show-word-limit
                                placeholder="Your Company, Inc."
                                @input="preValidateForm('bcidForm')">
                            </el-input>
                        </el-form-item>

                        <!-- Enhanced-only fields -->
                        <template v-if="form.type === TYPE_ENHANCED">
                            <el-form-item prop="display_call_reason">
                                <template slot="label">
                                    Display Call Reason
                                    <el-tooltip class="item" effect="dark" content="Max 64 characters. Describes the purpose of the call shown to the recipient." placement="top">
                                        <i class="fa fa-info-circle text-blue"/>
                                    </el-tooltip>
                                </template>
                                <el-input
                                    v-model="form.display_call_reason"
                                    maxlength="64"
                                    show-word-limit
                                    placeholder="e.g. Account notification"
                                    @input="preValidateForm('bcidForm')">
                                </el-input>
                            </el-form-item>

                            <el-form-item prop="display_logo_url">
                                <template slot="label">
                                    Display Logo URL
                                    <el-tooltip class="item" effect="dark" content="Public HTTPS URL to your logo image. Must be a 32-bit BMP, 256x256 pixels." placement="top">
                                        <i class="fa fa-info-circle text-blue"/>
                                    </el-tooltip>
                                </template>
                                <el-input
                                    v-model="form.display_logo_url"
                                    placeholder="https://example.com/logo.png"
                                    @input="preValidateForm('bcidForm')"
                                    @blur="() => { display_logo_url = display_logo_url; preValidateForm('bcidForm'); }">
                                </el-input>
                            </el-form-item>

                            <el-form-item prop="privacy_notice_url">
                                <template slot="label">
                                    Privacy Policy URL
                                    <el-tooltip class="item" effect="dark" content="Your company's publicly accessible privacy policy URL." placement="top">
                                        <i class="fa fa-info-circle text-blue"/>
                                    </el-tooltip>
                                </template>
                                <el-input
                                    v-model="form.privacy_notice_url"
                                    placeholder="https://example.com/privacy"
                                    @input="preValidateForm('bcidForm')"
                                    @blur="() => { privacy_notice_url = privacy_notice_url; preValidateForm('bcidForm'); }">
                                </el-input>
                            </el-form-item>

                        </template>

                        <!-- Lines selector -->
                        <el-form-item prop="line_ids" label="Lines">
                            <p class="line-height-normal">Select the lines you want to register for Branded Caller ID.</p>
                            <campaign-selector
                                multiple
                                clearable
                                only_branded_caller_id
                                show_all_lines_option
                                :value="form.line_ids"
                                @change="onLineIdsChange"
                                @input="onLineIdsChange"
                            />
                        </el-form-item>
                    </el-form>

                    <div class="mt-4 justify-content-end d-flex">
                        <el-button @click="goToStep(1)">
                            Back
                        </el-button>
                        <el-button
                            type="primary"
                            :disabled="!validated"
                            @click="goToStep(3)">
                            Next
                        </el-button>
                    </div>
                </el-card>
            </div>
            <div class="col-md-5">
                <div class="bcid-preview-sticky">
                    <branded-caller-id-preview
                        :type="form.type"
                        :company-name="form.display_name"
                        :display-long-name="form.display_long_name"
                        :call-reason="form.display_call_reason"
                        :logo-url="form.display_logo_url"
                    />
                </div>
            </div>
        </div>

        <!-- Step 3: Use Case & LOA -->
        <div
            v-if="currentStep === 3"
            class="row mt-2 border p-3 justify-content-center">
            <div class="col-md-6 mb-4 mb-md-0">
                <el-card class="border-0" shadow="never">
                    <div slot="header">
                        <strong>Use Case & Letter of Authorization</strong>
                    </div>
                    <el-form
                        ref="bcidUseCaseForm"
                        :model="form"
                        :rules="useCaseFormRules"
                        label-position="top"
                        class="form-aloware">

                        <!-- Use Case (Enhanced only) -->
                        <template v-if="form.type === TYPE_ENHANCED">
                            <div class="mb-2 border-bottom pb-2">
                                <strong>Use Case</strong>
                            </div>

                            <el-form-item prop="use_case_category">
                                <template slot="label">
                                    Category
                                    <el-tooltip class="item" effect="dark" content="Select the category that best describes the purpose of your outbound calls." placement="top">
                                        <i class="fa fa-info-circle text-blue"/>
                                    </el-tooltip>
                                </template>
                                <el-select
                                    v-model="form.use_case_category"
                                    placeholder="Select a use case category"
                                    class="w-100"
                                    @change="preValidateForm('bcidUseCaseForm')">
                                    <el-option
                                        v-for="cat in useCaseCategories"
                                        :key="cat.value"
                                        :label="cat.label"
                                        :value="cat.value"/>
                                </el-select>
                            </el-form-item>

                            <el-form-item prop="use_case_description">
                                <template slot="label">
                                    Use Case Description
                                    <el-tooltip class="item" effect="dark" content="Explain how you intend to use Branded Calling." placement="top">
                                        <i class="fa fa-info-circle text-blue"/>
                                    </el-tooltip>
                                </template>
                                <el-input
                                    v-model="form.use_case_description"
                                    type="textarea"
                                    :rows="3"
                                    placeholder="Describe how you will use branded calling..."
                                    @input="preValidateForm('bcidUseCaseForm')">
                                </el-input>
                            </el-form-item>

                            <el-form-item prop="use_case_consent_description">
                                <template slot="label">
                                    Consent Description
                                    <el-tooltip class="item" effect="dark" content="Describe how your recipients have opted in or consented to receive calls from you." placement="top">
                                        <i class="fa fa-info-circle text-blue"/>
                                    </el-tooltip>
                                </template>
                                <el-input
                                    v-model="form.use_case_consent_description"
                                    type="textarea"
                                    :rows="3"
                                    placeholder="Describe how recipients have consented to receive calls..."
                                    @input="preValidateForm('bcidUseCaseForm')">
                                </el-input>
                            </el-form-item>

                            <el-form-item>
                                <template slot="label">
                                    Apple Business Connect Category (optional)
                                    <el-tooltip class="item" effect="dark" content="Optional. Maps your use case to an Apple Business Connect category." placement="top">
                                        <i class="fa fa-info-circle text-blue"/>
                                    </el-tooltip>
                                </template>
                                <el-input
                                    v-model="form.use_case_category_apple"
                                    placeholder="Optional Apple category">
                                </el-input>
                            </el-form-item>
                        </template>

                        <!-- LOA file upload -->
                        <div class="mb-2 border-bottom pb-2" v-if="form.type === TYPE_ENHANCED">
                            <strong>Letter of Authorization</strong>
                        </div>
                        <el-form-item prop="loa_file">
                            <template slot="label">
                                Letter of Authorization (LOA)
                                <el-tooltip
                                    class="item"
                                    effect="dark"
                                    content="Upload a signed LOA document (PDF, JPG, JPEG, or PNG). Maximum file size: 2MB."
                                    placement="top">
                                    <i class="fa fa-info-circle text-blue"/>
                                </el-tooltip>
                            </template>
                            <el-upload
                                ref="loaUpload"
                                :auto-upload="false"
                                :limit="1"
                                :on-change="handleFileChange"
                                :on-remove="handleFileRemove"
                                :file-list="fileList"
                                accept=".pdf,.jpg,.jpeg,.png"
                                action="#"
                                class="my-3">
                                <el-button size="small" type="primary">
                                    <i class="fa fa-upload mr-1"></i>
                                    Select LOA File
                                </el-button>
                                <div slot="tip" class="el-upload__tip">
                                    Supported formats: PDF, JPG, JPEG, PNG (Max: 2MB)<br>
                                    <a href="https://cdn.prod.website-files.com/67ac801aadc0bbd4b1d89967/69d515dbbb0a7c864b94e6df_Aloware_Branded_Calling_LOA%20(1).pdf" target="_blank" class="text-blue">
                                        <i class="fa fa-download mr-1"></i>Download LOA sample template
                                    </a>
                                </div>
                            </el-upload>
                        </el-form-item>
                    </el-form>

                    <div class="mt-4 justify-content-end d-flex">
                        <el-button @click="goToStep(2)">
                            Back
                        </el-button>
                        <el-button
                            type="primary"
                            :disabled="!validated"
                            @click="goToStep(4)">
                            Next
                        </el-button>
                    </div>
                </el-card>
            </div>
            <div class="col-md-5">
                <div class="bcid-preview-sticky">
                    <branded-caller-id-preview
                        :type="form.type"
                        :company-name="form.display_name"
                        :display-long-name="form.display_long_name"
                        :call-reason="form.display_call_reason"
                        :logo-url="form.display_logo_url"
                    />
                </div>
            </div>
        </div>

        <!-- Step 4: Confirm & Submit -->
        <div
            v-if="currentStep === 4"
            class="mt-2 border d-flex row p-3 justify-content-center">
            <div class="col-md-6 d-flex flex-column justify-content-center">
                <h4 class="mb-4">Review & Submit</h4>

                <!-- Order Summary -->
                <el-card shadow="never" class="mb-3">
                    <div slot="header">
                        <strong><i class="fa fa-clipboard-list mr-2"></i>Order Summary</strong>
                    </div>
                    <table class="table table-sm mb-0">
                        <tbody>
                            <tr>
                                <td class="text-muted" style="width: 40%;">Type</td>
                                <td>
                                    <el-tag :type="form.type === TYPE_ENHANCED ? 'primary' : 'info'" size="small">
                                        {{ form.type === TYPE_ENHANCED ? 'Enhanced' : 'Standard' }} Branded Caller ID
                                    </el-tag>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-muted">Display Name</td>
                                <td class="font-weight-bold">{{ form.display_name }}</td>
                            </tr>
                            <tr v-if="form.type === TYPE_STANDARD && form.display_long_name">
                                <td class="text-muted">Long Display Name</td>
                                <td>{{ form.display_long_name }}</td>
                            </tr>
                            <tr v-if="form.type === TYPE_ENHANCED && form.display_logo_url">
                                <td class="text-muted">Brand Logo</td>
                                <td><a :href="form.display_logo_url" target="_blank" class="text-blue">Link</a></td>
                            </tr>
                            <tr v-if="form.type === TYPE_ENHANCED && form.display_call_reason">
                                <td class="text-muted">Call Reason</td>
                                <td>{{ form.display_call_reason }}</td>
                            </tr>
                            <tr v-if="form.type === TYPE_ENHANCED && form.use_case_category">
                                <td class="text-muted">Use Case</td>
                                <td>{{ useCaseCategoryLabel }}</td>
                            </tr>
                            <tr v-if="form.line_ids && form.line_ids.length > 0">
                                <td class="text-muted">Lines</td>
                                <td>{{ form.line_ids.includes('all') ? 'All Lines' : form.line_ids.length + ' line(s) selected' }}</td>
                            </tr>
                            <tr>
                                <td class="text-muted">LOA</td>
                                <td>
                                    <span v-if="form.loa_file" class="text-success">
                                        <i class="fa fa-check-circle mr-1"></i>{{ form.loa_file.name }}
                                    </span>
                                    <span v-else class="text-danger">
                                        <i class="fa fa-times-circle mr-1"></i>Not uploaded
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </el-card>

                <!-- Pricing -->
                <el-card shadow="never" class="mb-3">
                    <div slot="header">
                        <strong><i class="fa fa-dollar-sign mr-2"></i>Pricing</strong>
                    </div>
                    <div v-if="current_company && current_company.rate">
                        <table class="table table-sm mb-3">
                            <thead class="bg-light">
                                <tr>
                                    <th>Fee</th>
                                    <th class="text-right">Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        Submission Fee
                                        <div class="text-muted small">One-time fee for Branded Caller ID registration</div>
                                    </td>
                                    <td class="text-right font-weight-bold" style="font-size: 16px;">$1,000.00</td>
                                </tr>
                                <tr>
                                    <td>
                                        {{ form.type === TYPE_ENHANCED ? 'Enhanced' : 'Standard' }} Branded Call Rate
                                        <div class="text-muted small">Per call, deducted from your credit balance</div>
                                    </td>
                                    <td class="text-right font-weight-bold" style="font-size: 16px;">
                                        ${{ formatPrice(form.type === TYPE_ENHANCED ? current_company.rate.enhanced_branded_call : current_company.rate.branded_call) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <p class="mb-0">
                            <b>Your Rate Tier:</b>
                            <el-tag class="ml-2" size="small">
                                {{ current_company.rate.name }}
                            </el-tag>
                        </p>
                    </div>
                </el-card>

                <!-- Timeline -->
                <el-alert
                    type="info"
                    :closable="false"
                    show-icon
                    class="mb-4">
                    <template slot="title"><strong>What happens next?</strong></template>
                    Our team will review your submission and handle the registration process with carriers. Please allow up to 20 business days for review and approval. The $1,000 submission fee will be charged to your account.
                </el-alert>

                <div class="d-flex justify-content-between">
                    <el-button @click="goToStep(3)">
                        Back
                    </el-button>
                    <el-button
                        type="primary"
                        :loading="loading"
                        :disabled="hasOrder"
                        @click="submitOrder">
                        Submit {{ form.type === TYPE_ENHANCED ? 'Enhanced' : 'Standard' }} Branded Caller ID Order
                    </el-button>
                </div>
            </div>
            <div class="col-md-5">
                <div class="bcid-preview-sticky">
                    <branded-caller-id-preview
                        :type="form.type"
                        :company-name="form.display_name"
                        :display-long-name="form.display_long_name"
                        :call-reason="form.display_call_reason"
                        :logo-url="form.display_logo_url"
                    />
                </div>
            </div>
        </div>

        <div
            class="row"
            v-loading="loading_data"
            v-if="loading_data">
            <div class="col-12" style="min-height: 200px;"></div>
        </div>

        <!-- Confirmation Dialog -->
        <el-dialog
            title="Confirm Submission"
            :visible.sync="showConfirmationDialog"
            width="400px">
            <div class="text-center">
                <i class="fa fa-exclamation-circle text-warning mb-3" style="font-size: 48px;"></i>
                <p class="mb-0" style="font-size: 16px; word-break: keep-all; overflow-wrap: normal;">
                    Are you sure you want to submit your {{ form.type === TYPE_ENHANCED ? 'Enhanced' : 'Standard' }} Branded Caller ID order?
                </p>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="showConfirmationDialog = false">
                    Cancel
                </el-button>
                <el-button type="primary" :loading="loading" @click="confirmAndSubmit">
                    Yes, Submit
                </el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {form_validation_mixin} from '../../mixins'
import {TYPE_STANDARD, TYPE_ENHANCED, USE_CASE_CATEGORIES} from '../../constants/branded-caller-id-use-cases'
import CampaignSelector from '../campaign-selector.vue'
import BrandedCallerIdPreview from './branded-caller-id-preview.vue'

export default {
    name: 'BrandedCallerIdTab',

    mixins: [form_validation_mixin],

    components: {
        CampaignSelector,
        BrandedCallerIdPreview,
    },

    data() {
        return {
            currentStep: 1,
            loading: false,
            loading_data: true,
            has_order: false,
            showConfirmationDialog: false,
            fileList: [],
            TYPE_STANDARD,
            TYPE_ENHANCED,
            useCaseCategories: USE_CASE_CATEGORIES,
            form: {
                type: TYPE_ENHANCED,
                line_ids: [],
                display_name: '',
                display_long_name: '',
                loa_file: null,
                // Enhanced fields
                display_call_reason: '',
                display_logo_url: '',
                privacy_notice_url: '',
                use_case_category: '',
                use_case_description: '',
                use_case_consent_description: '',
                use_case_category_apple: '',
            },
        }
    },

    computed: {
        ...mapState('cache', ['current_company']),

        hasOrder() {
            return this.has_order
        },

        display_logo_url: {
            get() {
                return this.form.display_logo_url
            },
            set(value) {
                this.form.display_logo_url = this.transformUrl(value)
            },
        },

        privacy_notice_url: {
            get() {
                return this.form.privacy_notice_url
            },
            set(value) {
                this.form.privacy_notice_url = this.transformUrl(value)
            },
        },

        useCaseCategoryLabel() {
            const cat = this.useCaseCategories.find(c => c.value === this.form.use_case_category)
            return cat ? cat.label : this.form.use_case_category
        },

        formRules() {
            const isEnhanced = this.form.type === TYPE_ENHANCED
            const rules = {
                type: [{required: true, message: 'Please select a type', trigger: 'change'}],
                line_ids: [{
                    type: 'array',
                    required: false,
                    trigger: 'change',
                }],
                display_name: [
                    {required: true, message: 'Display name is required', trigger: 'blur'},
                    {
                        min: 2,
                        max: isEnhanced ? 35 : 15,
                        message: `Display name must be between 2 and ${isEnhanced ? 35 : 15} characters`,
                        trigger: 'blur'
                    },
                    {
                        // Must start with a letter. Only letters, numbers, periods, commas, and spaces. Max 35 chars (enhanced) / 15 chars (standard).
                        pattern: isEnhanced
                            ? /^[a-zA-Z][A-Za-z0-9,.\s]{1,34}$/
                            : /^[a-zA-Z][A-Za-z0-9,.\s]{1,14}$/,
                        message: 'Display name must start with a letter and can only contain letters, numbers, periods, commas, and spaces',
                        trigger: 'blur'
                    },
                ],
            }

            if (!isEnhanced) {
                rules.display_long_name = [
                    {required: true, message: 'Display long name is required', trigger: 'blur'},
                    {min: 2, max: 32, message: 'Display long name must be between 2 and 32 characters', trigger: 'blur'},
                    {
                        // Must start with a letter. Only letters, numbers, periods, commas, and spaces. Max 32 chars.
                        pattern: /^[a-zA-Z][A-Za-z0-9,.\s]{1,31}$/,
                        message: 'Display long name must start with a letter and can only contain letters, numbers, periods, commas, and spaces',
                        trigger: 'blur'
                    },
                ]
            } else {
                rules.display_call_reason = [
                    {required: true, message: 'Display call reason is required', trigger: 'blur'},
                    {max: 64, message: 'Display call reason may not be greater than 64 characters', trigger: 'blur'},
                    {
                        // Only letters, numbers, periods, commas, and spaces. Max 64 chars. No leading-letter requirement (matches Twilio schema).
                        pattern: /^[A-Za-z0-9., ]{1,64}$/,
                        message: 'Display call reason can only contain letters, numbers, periods, commas, and spaces',
                        trigger: 'blur',
                    },
                ]
                rules.display_logo_url = [
                    {required: true, message: 'Display logo URL is required', trigger: 'blur'},
                    {
                        validator: (rule, value, callback) => {
                            if (typeof value !== 'string' || !value.startsWith('https://')) {
                                callback(new Error('Display logo URL must start with https://'))
                            } else if (value === 'https://') {
                                callback(new Error('Display logo URL must include a domain after https://'))
                            } else if (!/^https:\/\/\S+\.\S+$/.test(value)) {
                                callback(new Error('Display logo URL must be a valid URL'))
                            } else {
                                callback()
                            }
                        },
                        trigger: 'blur',
                    },
                ]
                rules.privacy_notice_url = [
                    {required: true, message: 'Privacy notice URL is required', trigger: 'blur'},
                    {
                        validator: (rule, value, callback) => {
                            if (typeof value !== 'string' || !value.startsWith('https://')) {
                                callback(new Error('Privacy notice URL must start with https://'))
                            } else if (value === 'https://') {
                                callback(new Error('Privacy notice URL must include a domain after https://'))
                            } else if (!/^https:\/\/\S+\.\S+$/.test(value)) {
                                callback(new Error('Privacy notice URL must be a valid URL'))
                            } else {
                                callback()
                            }
                        },
                        trigger: 'blur',
                    },
                ]
            }

            return rules
        },

        useCaseFormRules() {
            const rules = {
                loa_file: [
                    {required: true, message: 'Please upload a Letter of Authorization (LOA) file', trigger: 'change'},
                ],
            }

            if (this.form.type === TYPE_ENHANCED) {
                rules.use_case_category = [
                    {required: true, message: 'Use case category is required', trigger: 'change'},
                ]
                rules.use_case_description = [
                    {required: true, message: 'Use case description is required', trigger: 'blur'},
                ]
                rules.use_case_consent_description = [
                    {required: true, message: 'Consent description is required', trigger: 'blur'},
                ]
            }

            return rules
        },
    },

    created() {
        this.fetchOrder()
        this.$nextTick(() => {
            this.preValidateForm('bcidForm')
        })
    },

    methods: {
        ...mapActions('cache', ['setCurrentCompany']),

        fetchOrder() {
            this.loading_data = true

            this.$APIV2.BrandedCallerIdOrders.fetchBrandedCallerIdOrders()
                .then(res => {
                    this.has_order = res.data.has_order
                })
                .catch(err => {
                    this.$root.handleErrors(err.response)
                })
                .finally(() => {
                    this.loading_data = false
                })
        },

        selectType(type) {
            this.form.type = type
            this.onTypeChange()
        },

        onTypeChange() {
            this.$nextTick(() => {
                if (this.$refs.bcidForm) {
                    this.$refs.bcidForm.clearValidate()
                }
                this.preValidateForm('bcidForm')
            })
        },

        onLineIdsChange(line_ids) {
            this.form.line_ids = line_ids || []
        },

        handleFileChange(file, fileList) {
            this.form.loa_file = file.raw
            this.fileList = fileList
            this.$nextTick(() => {
                if (this.$refs.bcidUseCaseForm) {
                    this.$refs.bcidUseCaseForm.validateField('loa_file')
                }
                this.preValidateForm('bcidUseCaseForm')
            })
        },

        handleFileRemove() {
            this.form.loa_file = null
            this.fileList = []
            this.$nextTick(() => {
                if (this.$refs.bcidUseCaseForm) {
                    this.$refs.bcidUseCaseForm.validateField('loa_file')
                }
                this.preValidateForm('bcidUseCaseForm')
            })
        },

        goToStep(step) {
            if (step === 3 && this.currentStep === 2) {
                this.$refs.bcidForm.validate((valid) => {
                    if (!valid) {
                        this.$message.error('Please fill in all required fields correctly')
                        return
                    }
                    this.currentStep = step
                    this.$nextTick(() => {
                        this.preValidateForm('bcidUseCaseForm')
                    })
                })
                return
            }

            if (step === 4 && this.currentStep === 3) {
                this.$refs.bcidUseCaseForm.validate((valid) => {
                    if (!valid) {
                        this.$message.error('Please fill in all required fields correctly')
                        return
                    }
                    this.currentStep = step
                })
                return
            }

            this.currentStep = step
        },

        submitOrder() {
            this.showConfirmationDialog = true
        },

        confirmAndSubmit() {
            this.loading = true

            const formData = new FormData()
            formData.append('type', this.form.type)
            formData.append('display_name', this.form.display_name)
            formData.append('loa_file', this.form.loa_file)
            this.form.line_ids.forEach((id) => {
                formData.append('line_ids[]', id)
            })

            if (this.form.type === TYPE_STANDARD) {
                formData.append('display_long_name', this.form.display_long_name)
            } else {
                formData.append('display_call_reason', this.form.display_call_reason)
                formData.append('display_logo_url', this.form.display_logo_url)
                formData.append('privacy_notice_url', this.form.privacy_notice_url)
                formData.append('use_case[category]', this.form.use_case_category)
                formData.append('use_case[description]', this.form.use_case_description)
                formData.append('use_case[consent_description]', this.form.use_case_consent_description)
                if (this.form.use_case_category_apple) {
                    formData.append('use_case[category_apple]', this.form.use_case_category_apple)
                }
            }

            this.$APIV2.BrandedCallerIdOrders.createBrandedCallerIdOrder(formData)
                .then(res => {
                    this.$notify({
                        offset: 95,
                        title: 'Success',
                        message: 'Your Branded Caller ID order has been submitted successfully. Our team will review and process your request.',
                        type: 'success',
                        showClose: true,
                        duration: 5000
                    })

                    this.has_order = true
                    this.showConfirmationDialog = false
                    this.setCurrentCompany(res.data.company)
                    this.goToStep(1)
                })
                .catch(err => {
                    this.$root.handleErrors(err.response)
                })
                .finally(() => {
                    this.loading = false
                })
        },

        formatPrice(price) {
            return parseFloat(price).toFixed(4)
        },
    },
}
</script>

<style scoped>
.bcid-type-card {
    border: 2px solid #e4e7ed;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.bcid-type-card:hover {
    border-color: #c0c4cc;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.bcid-type-card--selected {
    border-color: #409eff;
    background-color: #f0f7ff;
    box-shadow: 0 2px 12px rgba(64, 158, 255, 0.15);
}

.bcid-type-card--enhanced.bcid-type-card--selected {
    border-color: #409eff;
    border-width: 2px;
}

.bcid-feature-list {
    list-style: none;
    padding-left: 0;
    margin-bottom: 0;
}

.bcid-feature-list li {
    padding: 4px 0;
    font-size: 14px;
}

.bcid-requirements-list {
    list-style: none;
    padding-left: 0;
}

.bcid-requirements-list li {
    padding: 6px 0;
    font-size: 14px;
    display: flex;
    align-items: center;
}

.bcid-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 10px;
    flex-shrink: 0;
}

.flex-1 {
    flex: 1;
}

.bcid-phone-preview {
    flex-shrink: 0;
    width: 180px;
}

.bcid-phone-preview-inner {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 12px;
}

.bcid-phone-call-card {
    background: #fff;
    border-radius: 10px;
    color: #333;
}

.bcid-avatar {
    width: 32px;
    height: 32px;
    background: #409eff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
}

.bcid-btn-decline {
    font-size: 10px;
    color: #f56c6c;
    border: 1px solid #f56c6c;
    border-radius: 12px;
    padding: 2px 12px;
}

.bcid-btn-answer {
    font-size: 10px;
    color: #333;
    border: 1px solid #dcdfe6;
    border-radius: 12px;
    padding: 2px 12px;
}

.bcid-preview-sticky {
    position: sticky;
    top: 80px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 20px;
}

.bcid-need-tag {
    display: inline-flex;
    align-items: center;
    background: #f0f5ff;
    border: 1px solid #e4ecf7;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 13px;
}

.bcid-hero-card {
    background: linear-gradient(135deg, #1a2744 0%, #2c4a7c 50%, #3a6098 100%);
    border-radius: 16px;
    color: #fff;
}

.text-white-muted {
    color: rgba(255, 255, 255, 0.8);
}

.bcid-stat-card {
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 20px 28px;
    text-align: center;
    flex: 1;
}

.bcid-stat-number {
    font-size: 32px;
    font-weight: 700;
    color: #fff;
    line-height: 1.2;
}

.bcid-stat-label {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.7);
    margin-top: 4px;
}

.bcid-hero-link {
    display: inline-block;
    color: #fff;
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 20px;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
    transition: all 0.2s ease;
}

.bcid-hero-link:hover {
    background: rgba(255, 255, 255, 0.25);
    color: #fff;
    text-decoration: none;
}
</style>
